/**
 * LongCopy
 * @class
 * @constructor
 * @public
 */
class LongCopy {
  /**
   * Constructor
   * @returns {class}
   */
  constructor() {
    this.buttons = document.querySelectorAll('.long-copy__button--js');
    this.allowedCampaignParams = [
      'utm_medium',
      'utm_source',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'campaign_code',
      'ccode',
      'campaign_channel',
      'cchannel',
      'campaign_user',
      'cuser'
    ];

    this.init();
  }

  /**
   * Initialize the component
   * @returns {void}
   */
  init() {
    if (this.buttons.length <= 0) {
      return;
    }

    this.campaignParams = this._getCampaignParams();

    if (this.campaignParams.length <= 0) {
      return;
    }

    Array.prototype.forEach.call(this.buttons, (button) => {
      button.addEventListener('click', this._onButtonClick.bind(this));
    });

    return this;
  }

  /**
   * Update the target URL with current page campaign params before navigation.
   *
   * @param {Event} event
   * @returns {void}
   */
  _onButtonClick(event) {
    const button = event.currentTarget;
    const href = button.getAttribute('href');

    if (!href || this._shouldIgnoreHref(href)) {
      return;
    }

    button.setAttribute('href', this._appendCampaignParams(href));
  }

  /**
   * Collect all explicitly allowed campaign params from the current page URL.
   *
   * @returns {Array}
   */
  _getCampaignParams() {
    const queryStringCampaignParams = this._getCampaignParamsFromQueryString();

    if (queryStringCampaignParams.length > 0) {
      return queryStringCampaignParams;
    }

    return this._getCampaignParamsFromSession();
  }

  /**
   * Collect allowed campaign params from the current page query string.
   *
   * @returns {Array}
   */
  _getCampaignParamsFromQueryString() {
    if (!window.location.search || window.location.search.length <= 1) {
      return [];
    }

    const queryParams = window.location.search.substring(1).split('&');
    const campaignParams = [];

    for (let i = 0; i < queryParams.length; i++) {
      const queryParam = queryParams[i];

      if (!queryParam) {
        continue;
      }

      const splitIndex = queryParam.indexOf('=');
      const rawName = splitIndex > -1 ? queryParam.substring(0, splitIndex) : queryParam;
      const rawValue = splitIndex > -1 ? queryParam.substring(splitIndex + 1) : '';
      const paramName = this._decodeQueryValue(rawName).toLowerCase();
      const paramValue = this._decodeQueryValue(rawValue);

      if (!this._isAllowedCampaignParam(paramName)) {
        continue;
      }

      campaignParams.push({
        name: paramName,
        value: paramValue
      });
    }

    return campaignParams;
  }

  /**
   * Collect allowed campaign params from session storage.
   *
   * @returns {Array}
   */
  _getCampaignParamsFromSession() {
    if (!window.sessionStorage) {
      return [];
    }

    try {
      const storedParams = JSON.parse(window.sessionStorage.getItem('bobCampaignParams'));

      if (!storedParams || typeof storedParams !== 'object') {
        return [];
      }

      const campaignParams = [];
      const storedParamNames = Object.keys(storedParams);

      for (let i = 0; i < storedParamNames.length; i++) {
        const paramName = String(storedParamNames[i] || '').toLowerCase();

        if (!this._isAllowedCampaignParam(paramName)) {
          continue;
        }

        campaignParams.push({
          name: paramName,
          value: String(storedParams[storedParamNames[i]] || '')
        });
      }

      return campaignParams;
    } catch (e) {
      return [];
    }
  }

  /**
   * Append current campaign params while preserving other query params and hashes.
   *
   * @param {string} href
   * @returns {string}
   */
  _appendCampaignParams(href) {
    const hashIndex = href.indexOf('#');
    const hash = hashIndex > -1 ? href.substring(hashIndex) : '';
    const hrefWithoutHash = hashIndex > -1 ? href.substring(0, hashIndex) : href;
    const queryIndex = hrefWithoutHash.indexOf('?');
    const baseUrl = queryIndex > -1 ? hrefWithoutHash.substring(0, queryIndex) : hrefWithoutHash;
    const queryString = queryIndex > -1 ? hrefWithoutHash.substring(queryIndex + 1) : '';
    const queryParts = queryString ? queryString.split('&') : [];
    const filteredQueryParts = [];

    for (let i = 0; i < queryParts.length; i++) {
      const queryPart = queryParts[i];

      if (!queryPart) {
        continue;
      }

      const splitIndex = queryPart.indexOf('=');
      const rawName = splitIndex > -1 ? queryPart.substring(0, splitIndex) : queryPart;
      const paramName = this._decodeQueryValue(rawName).toLowerCase();

      if (this.allowedCampaignParams.indexOf(paramName) > -1) {
        continue;
      }

      filteredQueryParts.push(queryPart);
    }

    for (let i = 0; i < this.campaignParams.length; i++) {
      filteredQueryParts.push(
        encodeURIComponent(this.campaignParams[i].name) + '=' + encodeURIComponent(this.campaignParams[i].value)
      );
    }

    return baseUrl + (filteredQueryParts.length ? '?' + filteredQueryParts.join('&') : '') + hash;
  }

  /**
   * Safely decode query params and normalize `+` to spaces.
   *
   * @param {string} value
   * @returns {string}
   */
  _decodeQueryValue(value) {
    try {
      return decodeURIComponent((value || '').replace(/\+/g, ' '));
    } catch (e) {
      return value || '';
    }
  }

  /**
   * Ensure only explicitly allowed and safe campaign param names are used.
   *
   * @param {string} paramName
   * @returns {boolean}
   */
  _isAllowedCampaignParam(paramName) {
    return /^[a-z0-9_]+$/.test(paramName) && this.allowedCampaignParams.indexOf(paramName) > -1;
  }

  /**
   * Ignore links where campaign params are not applicable.
   *
   * @param {string} href
   * @returns {boolean}
   */
  _shouldIgnoreHref(href) {
    return href.indexOf('#') === 0 ||
      href.indexOf('mailto:') === 0 ||
      href.indexOf('tel:') === 0 ||
      href.indexOf('javascript:') === 0;
  }

  /**
   * Class name
   * @returns {string} The human readable class name
   */
  get name() {
    return 'LongCopy';
  }
};

export default LongCopy;
